import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { PlusIcon, SearchIcon, XIcon } from '@heroicons/react/outline';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  adminGiftBrandSellerList,
  adminGiftBrandSellerWelcomeSend,
} from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { Loading } from '@shared/components/Loading';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';

import styles from '../components/GiftBrandManagement.module.css';
import {
  GiftBrandSellerUpsertModal,
  SELLER_UPSERT_MODAL_ID,
  sellerBrands,
} from '../components/GiftBrandSellerUpsertModal';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';

const giftBrandLoginUrl = () =>
  `${process.env.REACT_APP_WEB_BASE_URL || window.location.origin}/gift-brand/login`;
type AccountFilter = 'all' | 'unassigned' | 'noPhone';
const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40';
const primaryButton =
  'inline-flex items-center justify-center gap-2 rounded bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40';
const cell = 'block px-5 py-2 xl:table-cell xl:py-5 xl:align-top';

export const GiftBrandSellerUserPage: FC = function GiftBrandSellerUserPage() {
  const { isAdmin, sellerRole } = useAuth();
  const canManage = isAdmin();
  const setUpsertOpen = useSetRecoilState(simpleOpenAtomFamily(SELLER_UPSERT_MODAL_ID));
  const [editTarget, setEditTarget] = useState<User | null>(null);
  useGiftItemBrandListHook(sellerRole(), !canManage);
  const brandStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);
  const [searchParams, setSearchParams] = useSearchParams();
  const brandFilterId = searchParams.get('brand');
  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftBrandSeller.search',
    '',
  );
  const [accountFilter, setAccountFilter] = useSessionPersistedState<AccountFilter>(
    'giftBrandSeller.filter',
    'all',
  );
  const brandNames = useMemo(
    () => new Map(brandList.map((brand) => [brand._id, brand.name])),
    [brandList],
  );
  const initialBrandId =
    brandFilterId && brandNames.has(brandFilterId) ? brandFilterId : null;
  const initialBrandIds = useMemo(
    () => (initialBrandId ? [initialBrandId] : undefined),
    [initialBrandId],
  );
  useEffect(() => {
    if (!brandFilterId) return;
    setSearchQuery('');
    setAccountFilter('all');
  }, [brandFilterId, setSearchQuery, setAccountFilter]);
  const setBrandFilter = (id: string) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('brand', id);
        else params.delete('brand');
        return params;
      },
      { replace: true },
    );
  const resetFilters = () => {
    setSearchQuery('');
    setAccountFilter('all');
    setBrandFilter('');
  };
  const [{ loading, error, value: userList }, fetchUsers] = useAsyncFn(
    async () => (canManage ? adminGiftBrandSellerList() : []),
    [canManage],
    { loading: true },
  );
  useEffect(() => {
    if (canManage) void fetchUsers();
  }, [fetchUsers, canManage]);
  const allRows = useMemo(() => userList ?? [], [userList]);
  const counts = {
    all: allRows.length,
    unassigned: allRows.filter((user) => sellerBrands(user).length === 0).length,
    noPhone: allRows.filter((user) => !user.info?.phoneNumber).length,
  };
  const rows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const phoneQuery = /^[\d\s()+-]+$/.test(query) ? query.replace(/\D/g, '') : '';
    return allRows.filter((user) => {
      const brands = sellerBrands(user);
      if (brandFilterId && !brands.some((brand) => brand.id === brandFilterId))
        return false;
      if (accountFilter === 'unassigned' && brands.length) return false;
      if (accountFilter === 'noPhone' && user.info?.phoneNumber) return false;
      return (
        !query ||
        [
          user.username,
          user.info?.name,
          ...brands.map((brand) => brandNames.get(brand.id) ?? brand.name),
        ].some((value) => value?.toLowerCase().includes(query)) ||
        Boolean(
          phoneQuery && user.info?.phoneNumber?.replace(/\D/g, '').includes(phoneQuery),
        )
      );
    });
  }, [allRows, searchQuery, brandFilterId, accountFilter, brandNames]);
  const filtered = Boolean(searchQuery || brandFilterId || accountFilter !== 'all');
  const openCreate = () => {
    setEditTarget(null);
    setUpsertOpen(true);
  };
  const openEdit = (user: User) => {
    setEditTarget(user);
    setUpsertOpen(true);
  };

  // 로그인 안내 문자 발송 — 저장된 비밀번호가 그대로 실리므로 확인 후 발송
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const sendWelcome = useCallback(
    async (user: User) => {
      const phone = user.info?.phoneNumber;
      if (!phone) {
        notificateError(
          notiInstance,
          '전화번호가 없는 계정입니다. 수정에서 번호를 먼저 입력해주세요.',
        );
        return;
      }
      const loginUrl = giftBrandLoginUrl();
      if (
        !window.confirm(
          `${
            user.info?.name || user.username
          } (${phone}) 님께 로그인 안내 문자를 보냅니다.\n\n` +
            `주소: ${loginUrl}\n아이디: ${user.username}\n비밀번호: (현재 저장된 비밀번호)\n\n발송할까요?`,
        )
      )
        return;
      try {
        setSendingId(user._id);
        const res = await adminGiftBrandSellerWelcomeSend(user._id, loginUrl);
        if (res?.success) notificateSuccess(notiInstance, `발송 완료 (${res.to})`);
        else notificateError(notiInstance, `발송 실패: ${res?.msg ?? '알 수 없는 오류'}`);
      } catch (error) {
        console.error(error);
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          notificateError(
            notiInstance,
            '서버에 안내 발송 기능이 아직 배포되지 않았습니다.',
          );
        } else if (axios.isAxiosError(error) && error.response?.status === 400) {
          notificateError(
            notiInstance,
            String(error.response?.data?.message ?? '발송할 수 없는 계정입니다.'),
          );
        } else {
          notificateError(notiInstance, '발송에 실패했습니다.');
        }
      } finally {
        setSendingId(null);
      }
    },
    [notiInstance],
  );

  if (!canManage)
    return <div className="p-8 text-center text-gray-500">관리자 전용 기능입니다.</div>;

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-[1440px]">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">배송관리자 계정</h1>
            <p className="mt-2 text-sm text-gray-500">
              계정 정보와 담당 브랜드를 확인하고, 로그인 안내를 보냅니다.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            disabled={brandStatus !== 'success'}
            className={primaryButton}
          >
            <PlusIcon aria-hidden className="h-4 w-4" />
            배송관리자 등록
          </button>
        </header>
        {brandStatus === 'error' && (
          <p
            role="alert"
            className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            브랜드 목록을 불러오지 못했습니다. 계정 등록·수정을 하려면{' '}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="underline"
            >
              다시 불러오기
            </button>
            를 눌러주세요.
          </p>
        )}
        <section
          aria-label="배송관리자 목록"
          className="rounded-md border border-gray-200 bg-white"
        >
          <div className="flex flex-col gap-4 px-5 pt-5 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div className="flex flex-wrap gap-1" aria-label="계정 관리 필터">
              {(
                [
                  { key: 'all', label: '전체' },
                  { key: 'unassigned', label: '브랜드 미지정' },
                  { key: 'noPhone', label: '전화번호 미등록' },
                ] as { key: AccountFilter; label: string }[]
              ).map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  aria-pressed={accountFilter === filter.key}
                  onClick={() => setAccountFilter(filter.key)}
                  className={`flex items-center gap-2 rounded px-3 py-2 text-sm ${
                    accountFilter === filter.key
                      ? 'bg-gray-900 font-medium text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {filter.label}
                  <span
                    className={`text-xs tabular-nums ${
                      accountFilter === filter.key ? 'text-gray-300' : 'text-gray-400'
                    }`}
                  >
                    {loading && !userList ? '–' : counts[filter.key]}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                aria-label="담당 브랜드 필터"
                value={brandFilterId ?? ''}
                disabled={brandStatus !== 'success'}
                onChange={(event) => setBrandFilter(event.target.value)}
                className="h-10 min-w-0 rounded border border-gray-300 bg-white px-3 text-sm sm:w-44"
              >
                <option value="">전체 브랜드</option>
                {brandFilterId && !brandNames.has(brandFilterId) && (
                  <option value={brandFilterId}>선택 브랜드</option>
                )}
                {brandList.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
              <div className="relative sm:w-72">
                <SearchIcon
                  aria-hidden
                  className="absolute left-3 top-3 h-4 w-4 text-gray-400"
                />
                <input
                  type="search"
                  aria-label="배송관리자 검색"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="이름, 아이디, 전화번호, 브랜드 검색"
                  className="h-10 w-full rounded border border-gray-300 pl-9 pr-10 text-sm [&::-webkit-search-cancel-button]:appearance-none"
                />
                {searchQuery && (
                  <button
                    type="button"
                    aria-label="검색어 지우기"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-1 top-1 rounded p-2 text-gray-400 hover:text-gray-700"
                  >
                    <XIcon aria-hidden className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
            <p role="status" className="text-xs text-gray-500">
              {loading && !userList ? (
                '불러오는 중'
              ) : error ? (
                '조회 실패'
              ) : (
                <>
                  총{' '}
                  <strong className="font-semibold text-gray-900">{rows.length}</strong>명
                  {filtered && ` / 전체 ${allRows.length}명`}
                  {brandFilterId &&
                    ` · ${brandNames.get(brandFilterId) ?? '선택 브랜드'} 담당`}
                </>
              )}
            </p>
            {filtered && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-gray-600 underline underline-offset-4"
              >
                필터 초기화
              </button>
            )}
          </div>
          {loading && !userList ? (
            <div className="flex justify-center border-t border-gray-200 py-20">
              <Loading />
            </div>
          ) : error ? (
            <div role="alert" className="border-t border-gray-200 px-5 py-16 text-center">
              <p className="font-medium">배송관리자 목록을 불러오지 못했습니다.</p>
              <button
                type="button"
                onClick={() => fetchUsers()}
                disabled={loading}
                className={`${secondaryButton} mt-5`}
              >
                {loading ? '불러오는 중…' : '다시 시도'}
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="border-t border-gray-200 px-5 py-20 text-center">
              <h2 className="font-medium">
                {filtered
                  ? '조건에 맞는 배송관리자가 없습니다'
                  : '첫 배송관리자를 등록해보세요'}
              </h2>
              <p className="mt-2 text-sm text-gray-500">
                {filtered
                  ? '검색 조건을 변경하거나 새 담당자를 등록하세요.'
                  : '계정을 등록하고 담당할 브랜드를 연결할 수 있습니다.'}
              </p>
              <button
                type="button"
                onClick={openCreate}
                disabled={brandStatus !== 'success'}
                className={`${primaryButton} mt-5`}
              >
                배송관리자 등록
              </button>
            </div>
          ) : (
            <table className="block w-full table-fixed text-left text-sm xl:table">
              <caption className="sr-only">배송관리자 계정, 연락처, 담당 브랜드</caption>
              <thead className="hidden border-t border-gray-200 bg-gray-50 xl:table-header-group">
                <tr>
                  {['계정 / 담당자', '연락처', '담당 브랜드', '관리'].map((heading) => (
                    <th
                      scope="col"
                      key={heading}
                      className={`px-5 py-3 text-xs font-medium text-gray-500 ${
                        heading === '관리' ? 'w-56 text-right' : ''
                      }`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="block xl:table-row-group">
                {rows.map((user) => (
                  <tr
                    key={user._id}
                    className="block border-t border-gray-200 py-4 hover:bg-gray-50/60 xl:table-row xl:py-0"
                  >
                    <th scope="row" className={cell}>
                      <button
                        type="button"
                        onClick={() => openEdit(user)}
                        disabled={brandStatus !== 'success'}
                        className="break-words rounded text-left text-base font-semibold text-gray-900 hover:text-indigo-700 hover:underline"
                      >
                        {user.info?.name || user.username}
                      </button>
                      <p className="mt-1 break-all text-xs font-normal text-gray-500">
                        {user.username}
                      </p>
                    </th>
                    <td className={cell}>
                      <span className="mr-2 text-xs text-gray-500 xl:hidden">
                        전화번호
                      </span>
                      {user.info?.phoneNumber ? (
                        <a
                          href={`tel:${user.info.phoneNumber}`}
                          className="rounded tabular-nums text-gray-700 hover:underline"
                        >
                          {user.info.phoneNumber}
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => openEdit(user)}
                          disabled={brandStatus !== 'success'}
                          className="rounded text-sm text-amber-700 hover:underline"
                        >
                          미등록 · 입력하기
                        </button>
                      )}
                    </td>
                    <td className={cell}>
                      <div className="flex flex-wrap gap-x-3 gap-y-2">
                        {sellerBrands(user).length ? (
                          sellerBrands(user).map((brand) => (
                            <Link
                              key={brand.id}
                              to={`/gift-seller/item-brand/${encodeURIComponent(
                                brand.id,
                              )}/edit`}
                              className="min-w-0 break-words rounded text-sm text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-indigo-700"
                            >
                              {brandNames.get(brand.id) ?? brand.name}
                              <span aria-hidden className="ml-1 text-gray-400">
                                ↗
                              </span>
                            </Link>
                          ))
                        ) : (
                          <button
                            type="button"
                            onClick={() => openEdit(user)}
                            disabled={brandStatus !== 'success'}
                            className="rounded text-sm text-amber-700 hover:underline"
                          >
                            미지정 · 브랜드 연결
                          </button>
                        )}
                      </div>
                    </td>
                    <td className={cell}>
                      <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                        <button
                          type="button"
                          onClick={() => openEdit(user)}
                          disabled={brandStatus !== 'success'}
                          className="shrink-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => sendWelcome(user)}
                          disabled={sendingId !== null || !user.info?.phoneNumber}
                          title={
                            user.info?.phoneNumber
                              ? '로그인 주소·아이디·비밀번호를 문자로 보냅니다'
                              : '전화번호를 먼저 등록해주세요'
                          }
                          className="shrink-0 rounded px-2 py-2 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                        >
                          {sendingId === user._id ? '발송 중…' : '로그인 안내 발송'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      <GiftBrandSellerUpsertModal
        target={editTarget}
        initialBrandIds={initialBrandIds}
        onSaved={fetchUsers}
      />
    </div>
  );
};
